import { createSelector } from 'reselect';

import { TRootState } from '@/redux/rootReducer';
import { IMeta, IVehicleType } from '@/services/types/search/rentals/id';
import { roundUpToNearest } from '@/utility/numbers';
import { keysToCamel } from '@/utility/objects';

type TSearchMeta = TRootState['search']['meta'];

export interface IMetaData {
  radius: number;
  lat: number;
  lng: number;
  addressLat?: number;
  addressLng?: number;
  city: string;
  county: string;
  state: string;
  country: string;
  countryCode: string;
  countryName: string;
  total: number;
  // Parents plus their child units; only sent for owner searches.
  totalUnits?: number;
  totalUnavailable: number;
  distance: number;
  startPosition: number;
  stopPosition: number;
  priceMax: number;
  priceMin: number;
  priceAverage: number;
  // The highest total trip price across the results, which the API only aggregates
  // for a dated search (the fetch sets `include_total_price_aggregation` from
  // `hasDates`). Its absence is therefore what distinguishes a dateless search,
  // where `priceMax` — a nightly rate — is the figure that matters instead.
  totalMaxPrice?: number;
  priceHistogram: {
    data: number[];
    maxValue: number;
  };
  vehicleTypes: IVehicleType[];
  locale: string;
  experiment: string;
  experiments: Record<string, string>;
  experimentsData: Record<string, Record<string, unknown>>;
  isBlended: boolean;
}

const emptyMeta: IMetaData = {
  radius: 0,
  lat: 0,
  lng: 0,
  state: '',
  city: '',
  county: '',
  country: '',
  countryCode: '',
  countryName: '',
  total: 0,
  totalUnavailable: 0,
  distance: 0,
  startPosition: 0,
  stopPosition: 0,
  priceMax: 0,
  priceMin: 0,
  priceAverage: 0,
  priceHistogram: {
    data: [],
    maxValue: 0,
  },
  vehicleTypes: [],
  locale: '',
  experiment: '',
  experiments: {},
  experimentsData: {},
  isBlended: false,
};

// $100, in cents — the increment the price filter's ceiling is rounded up to.
const PRICE_MAX_INCREMENT = 10_000;

/**
 * Narrows the price histogram's ceiling from the fixed one the API returns ($500 a
 * night, $10,000 a trip) down to the priciest result, rounded up to the next $100.
 * Those fixed ceilings leave the bars crushed into the left edge whenever the results
 * sit well below them (MP-6239).
 *
 * The buckets are trimmed to match. `Histogram` spreads them evenly across the axis
 * (`interval = maxValue / values.length`), so shortening the axis while keeping
 * buckets that sat beyond the new ceiling would misplace every bar. Bucket width is
 * whatever the API chose, so the last bucket kept can overhang the new ceiling a
 * little — a round $100 rarely lands exactly on a bucket edge.
 *
 * Only ever narrows. When there is no usable price to measure, or the API's own
 * ceiling is already the tighter of the two, the meta is handed back untouched.
 */
const withDynamicPriceMax = (meta: IMetaData): IMetaData => {
  const histogram = meta.priceHistogram;
  const apiMaxValue = histogram?.maxValue;
  // A dated search aggregates a total per rental, a dateless one only nightly rates;
  // see `totalMaxPrice` above for why its absence is the thing that distinguishes
  // them.
  const highestPrice = meta.totalMaxPrice || meta.priceMax;

  if (!apiMaxValue || !highestPrice || highestPrice <= 0) {
    return meta;
  }

  const dynamicMax = roundUpToNearest(highestPrice, PRICE_MAX_INCREMENT);

  if (dynamicMax >= apiMaxValue) {
    return meta;
  }

  const buckets = histogram.data;
  const canTrim = Array.isArray(buckets) && buckets.length > 0;
  const bucketWidth = canTrim ? apiMaxValue / buckets.length : 0;

  return {
    ...meta,
    priceHistogram: {
      ...histogram,
      // Left as it came when there is nothing to trim: an absent or empty bucket
      // list still gets the narrowed ceiling, because the inputs and slider read it
      // even when there are no bars to draw.
      ...(canTrim && { data: buckets.slice(0, Math.ceil(dynamicMax / bucketWidth)) }),
      maxValue: dynamicMax,
    },
  };
};

export const getSearchResultsMeta = createSelector<TRootState, TSearchMeta, IMetaData>(
  state => state.search.meta,
  meta => {
    if (!meta) {
      return emptyMeta;
    }

    return withDynamicPriceMax(keysToCamel(meta));
  },
);

export const getNearbyResultsMeta = createSelector<TRootState, IMeta | undefined, IMetaData>(
  state => state.search.nearby?.meta,
  meta => {
    if (!meta) return emptyMeta;
    return keysToCamel(meta);
  },
);
